import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiFetch, apiDownload } from "./api";
import { statusExpirare, textStare, formatData } from "../utils/expirare";
import { ETICHETE, statisticiFlota } from "../utils/flota";

export default function Dashboard() {
  const { client } = useAuth();
  const [masini, setMasini] = useState([]);
  const [stare, setStare] = useState("loading");
  const [flags, setFlags] = useState({ programari_viitoare: 0, facturi_neplatite: 0 });
  const [progApropiate, setProgApropiate] = useState([]);
  const [exportand, setExportand] = useState(false);

  useEffect(() => {
    apiFetch("/masini")
      .then((data) => {
        setMasini(data.masini || []);
        setStare("ok");
      })
      .catch(() => setStare("error"));
    apiFetch("/flags")
      .then((d) => { if (d && d.ok && d.flags) setFlags(d.flags); })
      .catch(() => {});
    apiFetch("/programari")
      .then((d) => {
        if (d && d.ok) {
          setProgApropiate((d.programari || []).filter((p) => p.status === "programata").slice(0, 3));
        }
      })
      .catch(() => {});
  }, []);

  async function descarcaRaport() {
    setExportand(true);
    try {
      const blob = await apiDownload("/export");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const azi = new Date().toISOString().slice(0, 10);
      a.download = `LEASEIN_raport_${azi}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "Nu am putut genera raportul.");
    } finally {
      setExportand(false);
    }
  }

  // Alerte (documente rosu/galben), sortate după urgență.
  const alerte = [];
  masini.forEach((m) => {
    Object.entries(m.expira).forEach(([tip, data]) => {
      const s = statusExpirare(data);
      if (s.stare === "rosu" || s.stare === "galben") {
        alerte.push({ id: m.id, masina: m.nrInmatriculare, tip, data, ...s });
      }
    });
  });
  alerte.sort((a, b) => a.zile - b.zile);

  const stats = stare === "ok" ? statisticiFlota(masini) : null;

  return (
    <div className="dash">
      <div className="dash__head dash__head--cu-actiune">
        <div>
          <h1>Bun venit, {client?.numeFirma}!</h1>
          <p>Iată o privire de ansamblu asupra flotei tale.</p>
        </div>
        <button
          className="btn btn--ghost dash__export"
          onClick={descarcaRaport}
          disabled={exportand}
        >
          {exportand ? "Se generează…" : "⬇ Descarcă raport Excel"}
        </button>
      </div>

      {(flags.programari_viitoare > 0 || flags.facturi_neplatite > 0) && (
        <div className="alert-bar">
          {flags.programari_viitoare > 0 && (
            <Link to="/portal/programari" className="alert-bar__item">
              🔧 Ai {flags.programari_viitoare}{" "}
              {flags.programari_viitoare === 1 ? "programare" : "programări"} la service/ITP
            </Link>
          )}
          {flags.facturi_neplatite > 0 && (
            <Link to="/portal/facturi" className="alert-bar__item alert-bar__item--rosu">
              💳 Ai {flags.facturi_neplatite}{" "}
              {flags.facturi_neplatite === 1 ? "factură neplătită" : "facturi neplătite"}
            </Link>
          )}
        </div>
      )}

      {stare === "loading" && <SkeletonDash />}

      {stare === "error" && (
        <div className="portal__loading">Nu am putut încărca datele.</div>
      )}

      {stare === "ok" && (
        <>
          <div className="dash__cards">
            <div className="dash__stat">
              <span className="dash__stat-num">{stats.total}</span>
              <span className="dash__stat-label">mașini în flotă</span>
            </div>
            <div className={`dash__stat ${stats.masiniOk < stats.total ? "" : "is-ok"}`}>
              <span className="dash__stat-num">{stats.masiniOk}</span>
              <span className="dash__stat-label">complet în regulă</span>
            </div>
            <div className={`dash__stat ${stats.masiniProbleme - stats.masiniExpirate ? "is-warn" : ""}`}>
              <span className="dash__stat-num">{stats.masiniProbleme - stats.masiniExpirate}</span>
              <span className="dash__stat-label">expiră curând</span>
            </div>
            <div className={`dash__stat ${stats.masiniExpirate ? "is-danger" : ""}`}>
              <span className="dash__stat-num">{stats.masiniExpirate}</span>
              <span className="dash__stat-label">cu documente expirate</span>
            </div>
          </div>

          {stats.conforme != null && (
            <div className="dash__section">
              <div className="dash__section-head">
                <h2>Conformitate flotă</h2>
                <span className="dash__pct">{stats.conforme}%</span>
              </div>
              <div className="bar">
                <div
                  className="bar__fill"
                  style={{ width: `${stats.conforme}%` }}
                />
              </div>
              <p className="dash__bar-note">
                {stats.docs.verde} din {stats.docsTotal} documente sunt valabile
                {stats.docs.necunoscut > 0
                  ? ` (${stats.docs.necunoscut} fără dată încărcată încă)`
                  : ""}.
              </p>

              <div className="breakdown">
                {Object.keys(ETICHETE).map((tip) => {
                  const t = stats.peTip[tip];
                  // v9.0: numitorul = toată flota; documentele lipsă apar cu
                  // galben ca „necunoscut" (nu ca expirat), deci 9/16, nu 9/9.
                  const total = stats.total;
                  return (
                    <div className="breakdown__row" key={tip}>
                      <span className="breakdown__nume">{ETICHETE[tip]}</span>
                      <div className="breakdown__bar">
                        {total === 0 ? (
                          <div className="breakdown__seg breakdown__seg--necunoscut" style={{ width: "100%" }} />
                        ) : (
                          <>
                            {t.verde > 0 && <div className="breakdown__seg breakdown__seg--verde" style={{ width: `${(t.verde / total) * 100}%` }} />}
                            {t.galben > 0 && <div className="breakdown__seg breakdown__seg--galben" style={{ width: `${(t.galben / total) * 100}%` }} />}
                            {t.rosu > 0 && <div className="breakdown__seg breakdown__seg--rosu" style={{ width: `${(t.rosu / total) * 100}%` }} />}
                            {t.necunoscut > 0 && <div className="breakdown__seg breakdown__seg--necunoscut" style={{ width: `${(t.necunoscut / total) * 100}%` }} />}
                          </>
                        )}
                      </div>
                      <span className="breakdown__cifre">
                        {t.rosu > 0 && <em className="is-rosu">{t.rosu}</em>}
                        {t.galben > 0 && <em className="is-galben">{t.galben}</em>}
                        {t.necunoscut > 0 && (
                          <em className="is-necunoscut" title="Fără dată încărcată încă">{t.necunoscut}?</em>
                        )}
                        <span>{t.verde}/{total || "—"}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="dash__section">
            <div className="dash__section-head">
              <h2>Atenționări</h2>
              <Link to="/portal/masini" className="dash__link">Vezi toate mașinile →</Link>
            </div>

            {alerte.length === 0 ? (
              <div className="dash__empty">Totul e în regulă — nimic nu expiră curând.</div>
            ) : (
              <ul className="dash__alerts">
                {alerte.slice(0, 8).map((a, i) => (
                  <li key={i} className={`dash__alert dash__alert--${a.stare}`}>
                    <span className={`dot dot--${a.stare}`} />
                    <Link to={`/portal/masini/${a.id}`} className="dash__alert-masina">{a.masina}</Link>
                    <span className="dash__alert-tip">{ETICHETE[a.tip]}</span>
                    <span className="dash__alert-text">{textStare(a.zile)}</span>
                    <span className="dash__alert-data">{formatData(a.data)}</span>
                  </li>
                ))}
                {alerte.length > 8 && (
                  <li className="dash__alert-more">
                    <Link to="/portal/masini">+ încă {alerte.length - 8} atenționări →</Link>
                  </li>
                )}
              </ul>
            )}
          </div>

          {progApropiate.length > 0 && (
            <div className="dash__section">
              <div className="dash__section-head">
                <h2>Următoarele programări</h2>
                <Link to="/portal/programari" className="dash__link">Vezi toate →</Link>
              </div>
              <ul className="dash__alerts">
                {progApropiate.map((p) => (
                  <li key={p.id} className="dash__alert dash__alert--galben">
                    <span className="dot dot--galben" />
                    <span className="dash__alert-masina">{p.nr_inmatriculare}</span>
                    <span className="dash__alert-tip">
                      {{ service: "Service", itp: "ITP", revizie: "Revizie", anvelope: "Anvelope", altele: "Altele" }[p.tip_lucrare] || p.tip_lucrare}
                    </span>
                    <span className="dash__alert-text">{p.locatie}</span>
                    <span className="dash__alert-data">
                      {formatData(p.data_prog)}{p.ora_prog ? ` ${p.ora_prog}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="dash__cta">
            <div>
              <h3>Ai nevoie de ceva?</h3>
              <p>Trimite o solicitare și revenim cât mai repede.</p>
            </div>
            <Link to="/portal/solicitari" className="btn btn--primary">Solicitare nouă</Link>
          </div>
        </>
      )}
    </div>
  );
}

function SkeletonDash() {
  return (
    <div aria-hidden="true">
      <div className="dash__cards">
        {[0, 1, 2, 3].map((i) => (
          <div className="dash__stat" key={i}>
            <span className="skel skel--line" style={{ width: 48, height: 34 }} />
            <span className="skel skel--line" style={{ width: "70%", height: 13, marginTop: 10 }} />
          </div>
        ))}
      </div>
      <div className="dash__section">
        <span className="skel skel--line" style={{ width: 160, height: 20 }} />
        <span className="skel skel--line" style={{ width: "100%", height: 12, marginTop: 16 }} />
      </div>
    </div>
  );
}
